import React, { useEffect, useState } from "react";
import { Event, Location } from "@bindeleddet/core/types";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import { Button, Card, Empty, Table } from "antd";
import dayjs from "dayjs";
import { getMainResponsible } from "helpers/functions";
import { formatDateWithTimeNorwegian } from "helpers/dateUtils";

interface Props {
  companyId: number;
}

const AllCompanyEvents: React.FC<Props> = ({ companyId }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [responsibleMembers, setResponsibleMembers] = useState<{ [key: number]: string }>({});
  const history = useHistory();

  useEffect(() => {
    axios
      .get<Event[]>(`/companies/${companyId}/events/`)
      .then(response => {
        setEvents(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [companyId]);

  useEffect(() => {
    const fetchResponsible = async (eventId: number) => {
      try {
        const mainResponsible = await getMainResponsible(eventId);
        return mainResponsible?.full_name ?? "Ukjent";
      } catch {
        return "Ukjent";
      }
    };

    const loadResponsibles = async () => {
      const responsiblePromises = events.map(event =>
        fetchResponsible(event.id).then(responsible => [event.id, responsible] as [number, string])
      );
      const responsibleEntries = await Promise.all(responsiblePromises);
      setResponsibleMembers(Object.fromEntries(responsibleEntries));
    };

    loadResponsibles();
  }, [events]);

  const eventColumns = [
    {
      title: "Visningsnavn",
      dataIndex: "name",
      ellipsis: true,
      render: (name: string, record: Event) => {
        return <Link to={`/admin/events/${record.id}`}>{name}</Link>;
      },
    },
    {
      title: "Ansvarlig",
      ellipsis: true,
      dataIndex: "id",
      render: (eventId: number) => responsibleMembers[eventId] ?? "Ukjent",
    },
    {
      title: "Tidspunkt",
      dataIndex: "start",
      render: (start: string) => formatDateWithTimeNorwegian(start),
      ellipsis: true,
    },
    {
      title: "Sted",
      dataIndex: "location",
      ellipsis: true,
      render: (location: Location | null) => location?.name ?? "Ikke bestemt",
    },
    {
      title: "Påmeldte",
      dataIndex: "registrations",
      ellipsis: true,
      render: (registrations: number, record: unknown) => {
        const event = record as Event;
        return dayjs().isBefore(dayjs(event.open)) ? "Ikke åpnet" : `${registrations}/${event.seats}`;
      },
    },
  ];

  return (
    <>
      <Card
        title="Arrangementer"
        extra={
          <Button type="primary" onClick={() => history.push(`/admin/events/new/${companyId}/`)}>
            Nytt arrangement
          </Button>
        }
      >
        <Table
          columns={eventColumns}
          dataSource={events.sort((a, b) => dayjs(b.start).diff(dayjs(a.start)))}
          size="small"
          rowKey="id"
          onRow={record => ({
            onClick: () => history.push(`/admin/events/${record.id}/`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ingen arrangementer" />,
          }}
          loading={loading}
          pagination={{ pageSize: 5 }}
        />
      </Card>
    </>
  );
};

export default AllCompanyEvents;
